import { IOrgAccount } from '@modules/admin-panel/domain/models/admin';
import { PartnerAliasEnum } from '@partners/partner-alias.enum';
import { PartnersEnum } from '@partners/partners.enum';
import * as lodash from 'lodash';
import moment, { Moment as IMoment } from 'moment';
import * as pako from 'pako';
import { Observable, of } from 'rxjs';
import { ApiUrlServices, CEMetrics } from '../interfaces';
import { Organization } from '../interfaces/accounts_list';

export const clonedeep = lodash.cloneDeep;

export const copyText = (val: string | number): void => {
	const selBox = document.createElement('textarea');
	selBox.style.position = 'fixed';
	selBox.style.left = '0';
	selBox.style.top = '0';
	selBox.style.opacity = '0';
	selBox.value = val.toString();
	document.body.appendChild(selBox);
	selBox.focus();
	selBox.select();
	document.execCommand('copy');
	document.body.removeChild(selBox);
};

export const getActiveOrganization = (orgList: Organization[]): Organization =>
	orgList.find((org) => !!org.is_selected);

export const getApiUrl = (apiUrlDomain: string, service?: ApiUrlServices): string => `${apiUrlDomain}${service || ''}`;

export interface IRoleUrlTemplateParams {
	productLabelPrefix: string;
	subject: string;
	region: string;
	permission: string;
	createDate: string;
	templateUrl: string;
	curBucket?: string;
}

export const getRoleUrlTemplate = (params: IRoleUrlTemplateParams): string => {
	const base = `https://eu-west-1.console.aws.amazon.com/cloudformation/home?region=${params.region}#/stacks/create/review?`;
	const queryUUIDToken = `param_ClientUUIDToken=${params.subject}`;
	const queryRegion = `param_AWSRegion=${params.region}`;
	let productLabel = params.productLabelPrefix;
	if (productLabel === 'cloudreach') {
		productLabel += '-zesty';
	}
	if (productLabel === 'midlink') {
		productLabel = 'midlink-finops';
	}
	if (productLabel === 'cloudvisor') {
		productLabel = 'zesty';
	}
	// specific condition for the whitelabels which name starts with a digit
	if (productLabel === PartnersEnum['2bcloud']) {
		productLabel = PartnerAliasEnum.TwoBCloud;
	}
	const queryStackName = `stackName=${productLabel}-iam-${params.permission}-role${params.createDate}`;
	const curBucket = `param_BucketName=${params.curBucket}`;
	const queryTemplateUrl = `templateURL=${params.templateUrl}`;
	return `${base}${queryRegion}&${queryUUIDToken}&${queryStackName}&${queryTemplateUrl}&${curBucket}`;
};

export const getYesterday = (data: CEMetrics[], key: string): { date: string; value: number } => {
	const now = new Date();
	const yyyy = now.getFullYear();
	let mm: string | number = now.getMonth() + 1;
	mm = mm < 10 ? `0${mm}` : mm;
	const yesterdayIndex = new Date().getDate() - 1;
	const yesterday = `${yyyy}-${mm}-${yesterdayIndex < 10 ? '0' + yesterdayIndex : yesterdayIndex}`;
	const valueData = data.find((metric) => metric.date === yesterday);

	return {
		date: yesterday,
		value: valueData ? valueData[key] : 0,
	};
};

export const getDayIndex = (date = new Date().toDateString()): number => {
	return new Date(date).getDate() - 1;
};

export const calcWeeksInMonth = (date: IMoment): number => {
	const dateFirst = moment(date).date(1);
	const dateLast = moment(date).date(date.daysInMonth());
	const startWeek = dateFirst.isoWeek();
	const endWeek = dateLast.isoWeek();

	if (endWeek < startWeek) {
		// Yearly overlaps, month is either DEC or JAN
		if (dateFirst.month() === 0) {
			// January
			return endWeek + 1;
		} else {
			// December
			if (dateLast.isoWeekday() === 7) {
				// Sunday is last day of year
				return endWeek - startWeek + 1;
			} else {
				// Sunday is NOT last day of year
				return dateFirst.isoWeeksInYear() - startWeek + 1;
			}
		}
	} else {
		return endWeek - startWeek + 1;
	}
};

export const bytesToHumanReadableGb = (value): string => (value / 1024 / 1024 / 1024).toFixed(2) + ' GB';

export const bytesToHumanReadable = (value: number, withLabel = false): string => {
	const size = value;

	let castedSize;
	let label;

	if (size.toString().length < 5) {
		castedSize = size / 1024;
		label = ' KB';
	}
	if (size.toString().length >= 5 && size.toString().length <= 8) {
		castedSize = size / 1048576;
		label = ' MB';
	}
	if (size.toString().length > 8 && size.toString().length <= 11) {
		castedSize = size / 1073741824;
		label = ' GB';
	}
	if (size.toString().length > 11) {
		castedSize = size / 1099511627776;
		label = ' TB';
	}

	return withLabel ? castedSize.toFixed(2) + label : castedSize.toFixed(2);
};

export function getOrgAccounts(accounts): IOrgAccount[] {
	const result: IOrgAccount[] = [];

	for (const key in accounts.AWS) {
		if (accounts.AWS.hasOwnProperty(key)) {
			const payer = accounts.AWS[key];
			if (payer.is_active) {
				result.push(...payer.linked_accounts.filter((acc) => acc.is_onboarded));
			}
		}
	}

	return result;
}

export function toCamelCase(propName: string): string {
	if (propName.includes('_')) {
		return propName.replace(/_([a-z])/g, (g) => {
			return g[1].toUpperCase();
		});
	} else if (propName[0] === propName[0].toUpperCase()) {
		// if propName is capitalized (PascalCase)
		return propName.replace(propName[0], propName[0].toLowerCase());
	} else {
		return propName;
	}
}

export function sortListByFieldAsc<T = unknown>(list: T[], param: string): T[] {
	return [...list].sort((a: T, b: T) => {
		if (a[param] && b[param]) {
			if (a[param].toLowerCase() > b[param].toLowerCase()) {
				return 1;
			} else if (a[param].toLowerCase() < b[param].toLowerCase()) {
				return -1;
			} else {
				return 0;
			}
		}
	});
}

/**
 * accepts array or object
 * formats prop names from snake_case to camelCase
 * don`t format nested properties for now
 * returns formatted array or object
 */
export function formatResponsePropNames(response: [] | any): [] | any {
	if (Array.isArray(response)) {
		return response.map((item) => {
			const formattedItem = {};
			Object.keys(item).forEach((prop) => {
				const newProp = toCamelCase(prop);
				formattedItem[newProp] = item[prop];
			});
			return formattedItem;
		});
	} else {
		const formattedItem = {};
		Object.keys(response).forEach((prop) => {
			const newProp = toCamelCase(prop);
			formattedItem[newProp] = response[prop];
		});
		return formattedItem;
	}
}

export function handleError<T>(err: any, data: T): Observable<T> {
	console.error('Error: ', err);
	return of(data);
}

export const logConsole = (logData: any, msg: string, type = 'info', isShown = true): void => {
	const color = type === 'info' ? '#ffc107' : '#fd397a';

	if (isShown) {
		console.log(`%c ${type.toUpperCase()} [${msg}]: `, `background: #001f38; color: ${color}`, logData);
	}

	return;
};

export const splitArnToName = (arn = ''): string => (arn && arn.split(':').length === 6 ? arn.split(':')[5] : arn);

export const isObjectEmpty = (object: {}): boolean => {
	return !(object && Object.keys(object)?.length);
};

export const transformToPercent = <T>(data: T[], key: string): T[] => {
	return data.map((item) => {
		return {
			...item,
			[key]: Math.floor(item[key] * 100) || '',
		};
	});
};

export const decompressGzip = <T>(gzip: string): T => {
	const gezipedData = atob(gzip);
	const gzipedDataArray = Uint8Array.from(gezipedData, (c) => c.charCodeAt(0));

	const ungzipedData = pako.ungzip(gzipedDataArray);
	return JSON.parse(new TextDecoder().decode(ungzipedData));
};

export function clearNullFields(obj: { [key: string]: any }) {
	for (const key in obj) {
		if (obj[key] === null) {
			delete obj[key];
		} else if (typeof obj[key] === 'object') {
			clearNullFields(obj[key]);
		}
	}
	return obj;
}
